import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  BeakerIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import _ from 'lodash'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { Menu, MenuButton, MenuItems } from '@headlessui/react'

// Images
import Inbox from '../../assets/images/inbox.svg'

// Files
import template from '../../assets/files/attendee_import_template.xlsx'

// Components
import { Button } from '../../components/Button'
import { DataTable } from '../../components/DataTable'
import { DownloadIcon } from '../../components/DownloadIcon'
import { EventHeader } from '../../components/EventHeader'
import { FileUploader } from '../../components/FileUploader'
import { Modal } from '../../components/Modal'
import { StateContainer } from '../../components/StateContainer'
import { SyncExternalAttendeeModal } from '../../components/SyncExternalAttendeeModal'
import { TextInput } from '../../components/TextInput'
import { Tooltip } from '../../components/Tooltip'
import AttendeeModal from './AttendeeModal'

// Store
import { NavigationStoreContext } from '../../stores/NavigationStore'
import { TaskStoreContext } from '../../stores/TaskStore'
import { UserStoreContext } from '../../stores/UserStore'

// Service
import {
  createAttendeeSync,
  getAttendees,
  getAttendeeSyncStatus,
  updateAttendee,
  uploadAttendeeList,
} from '../../services/attendees.service'
import { createCheckInExport } from '../../services/events.service'
import { getOrganizationApiOptions } from '../../services/organizations.service'

// Utils & Styles
import { formatDuration } from '../../utils/formatters'
import { handlePagination, joinClassNames, toast } from '../../utils/helpers'
import colors from '../../utils/colors'
import { hasCompletedRegistrationConfig } from '../EventApiConfig/helpers'
import SyncLogSlideout from '../EventApiConfig/SyncLogSlideout'

const TABS = [
  {
    id: 0,
    label: 'Primary Attendees',
  },
  {
    id: 1,
    label: 'Secondary Attendees',
  },
]

const CATEGORY_COLORS = [
  { background: colors.purple.DEFAULT, label: colors.white.DEFAULT },
  { background: colors.teal.DEFAULT, label: colors.white.DEFAULT },
  { background: colors.blue.DEFAULT, label: colors.white.DEFAULT },
  { background: colors.purple[900], label: colors.white.DEFAULT },
  { background: colors.teal[900], label: colors.white.DEFAULT },
  { background: colors.blue[900], label: colors.white.DEFAULT },
  { background: colors.purple.light, label: colors.gray.dark },
  { background: colors.teal.light, label: colors.gray.dark },
  { background: colors.blue.light, label: colors.gray.dark },
  { background: colors.purple.dark, label: colors.white.DEFAULT },
  { background: colors.teal.dark, label: colors.white.DEFAULT },
  { background: colors.blue.dark, label: colors.white.DEFAULT },
]

const BUTTON_STYLES =
  'font-bold text-blue-600 hover:rounded-full hover:bg-status-blue px-2 py-0.5 disabled:cursor-not-allowed disabled:opacity-50'

// Statuses that mean the sync task has not finished yet.
const IN_PROGRESS_SYNC_STATUSES = ['Pending', 'Processing', 'Retrying']

// How often to ask the API for the state of an in-flight single attendee sync.
const SINGLE_SYNC_POLL_MS = 3000

// Consecutive failed status reads before a single attendee sync stops being tracked.
const MAX_SINGLE_SYNC_POLL_MISSES = 3

/**
 *
 * EventAttendees
 *
 */
const EventAttendees = observer(() => {
  // Context
  const { task, setParent, setTask, setType } = useContext(TaskStoreContext)
  const { event, attendeeSyncTask, setAttendeeSyncTask } = useContext(NavigationStoreContext)
  const { isEEUser, isOrganizationAdmin, user } = useContext(UserStoreContext)
  const { eventId, orgId } = useParams()

  const readOnly = event ? event.enableRegistrationSync : false

  // State
  const [active, setActive] = useState(TABS[0])
  const [loading, setLoading] = useState(false)
  const [registrationOptions, setRegistrationOptions] = useState([])
  const [loadingAttendees, setLoadingAttendees] = useState(false)
  const [attendees, setAttendees] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [searchQuery, setSearchQuery] = useState(null)
  const [showAttendeeModal, setShowAttendeeModal] = useState(false)
  const [editAttendee, setEditAttendee] = useState(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [loadingImport, setLoadingImport] = useState(false)
  const [uploadFile, setUploadFile] = useState(null)
  const [importedFile, setImportedFile] = useState(null)
  const [categories, setCategories] = useState([])
  const [loadingCheckInExport, setLoadingCheckInExport] = useState(false)

  // Sync
  const [loadingSync, setLoadingSync] = useState(false)
  const [pingSync, setPingSync] = useState(false)
  const [singleAttendeeSyncs, setSingleAttendeeSyncs] = useState([])
  const [showSyncExternalAttendeeModal, setShowSyncExternalAttendeeModal] = useState(false)
  const [showSyncLog, setShowSyncLog] = useState(false)

  // Primary Pagination
  const [primaryCurrentPage, setPrimaryCurrentPage] = useState(1)
  const [primaryTotalRows, setPrimaryTotalRows] = useState(0)
  const [primaryPerPage, setPrimaryPerPage] = useState(20)
  const [primaryPages, setPrimaryPages] = useState(null)

  // Ref
  const searchInputRef = useRef(null)
  // Failed status reads per in-flight single attendee sync, keyed by sync id.
  const syncPollMissesRef = useRef({})

  const BASE_URL = `/events/${eventId}/attendees/?expand=sessions&`

  const handleErrors = (m) => toast(m, 'error')
  const handleSuccesses = (m) => toast(m, 'success')

  /**
   * Gets the updated list of attendees; updates pagination.
   * @param {string} url
   * @returns list of results
   */
  const getUpdatedAttendeeList = async (url) => {
    const response = await getAttendees(url, handleErrors, setLoadingAttendees, () => {})

    if (response) {
      setPrimaryTotalRows(response.count)
      setPrimaryPages({ next: response.next, previous: response.previous })
      setAttendees(response.results)
    }
  }

  useEffect(() => {
    const getUpdatedData = async () => {
      const optionsResponse = await getOrganizationApiOptions(orgId, handleErrors, setLoading)

      if (optionsResponse) {
        setRegistrationOptions(
          _.map(optionsResponse, (item) => ({
            label: item.system,
            id: item.system,
            type: item.authType,
          })),
        )
      }
    }

    if (event?.enableRegistrationSync) {
      getUpdatedData()
    }

    // Set up the sync ping if there is still one in progress
    if (attendeeSyncTask && !pingSync) setPingSync(true)

    // Configure category options
    const categoryOptions = _.map(_.keys(event?.attendeeCategories), (k) => ({
      id: k,
      label: _.capitalize(event.attendeeCategories[k]),
    }))
    setCategories(_.sortBy(categoryOptions))
  }, [])

  useEffect(() => {
    if (task && task.status === 'Imported') {
      getUpdatedAttendeeList(`${BASE_URL}is_secondary_attendee=False&limit=${primaryPerPage}`)
    }
  }, [task])

  useEffect(() => {
    let interval = null

    if (pingSync) {
      if (interval === null) {
        interval = setInterval(async () => {
          const response = await getAttendeeSyncStatus(eventId, attendeeSyncTask.id)

          if (response.status !== 'Pending' && response.status !== 'Processing') {
            clearInterval(interval)
            setAttendeeSyncTask(null)
            setPingSync(false)

            // Clear any searches
            filterAttendees('')
            setSearchTerm('')
            searchInputRef.current.value = ''
          } else {
            setAttendeeSyncTask(response)
          }
        }, 5000)
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [pingSync])

  /**
   * When the active tab, filter or row count changes, get the updated list of attendees.
   */
  useEffect(() => {
    // Primary attendees
    if (active.id === 0) {
      getUpdatedAttendeeList(
        `${BASE_URL}is_secondary_attendee=False&limit=${primaryPerPage}${
          searchQuery ? `&${searchQuery}` : ''
        }`,
      )
    }
    // Secondary attendees
    else {
      getUpdatedAttendeeList(
        `${BASE_URL}is_secondary_attendee=True&limit=${primaryPerPage}${
          searchQuery ? `&${searchQuery}` : ''
        }`,
      )
    }
  }, [active, searchQuery, primaryPerPage])

  /**
   * Builds the attendee list URL for the active tab, current page, per-page count,
   * and search query.
   * @returns {string}
   */
  const currentAttendeeListUrl = () => {
    const params = [
      `is_secondary_attendee=${active.id === 0 ? 'False' : 'True'}`,
      `limit=${primaryPerPage}`,
    ]
    if (searchQuery) params.push(searchQuery)
    if (primaryCurrentPage > 1) params.push(`page=${primaryCurrentPage}`)

    return `${BASE_URL}${params.join('&')}`
  }

  /**
   * Re-fetches the attendee list preserving the active tab, current page,
   * per-page count, and search query.
   */
  const refreshAttendeeList = () => {
    getUpdatedAttendeeList(currentAttendeeListUrl())
  }

  // The sync poll outlives the render that started it. Refresh through this ref so a
  // finished sync reloads the view the user is on now, not the tab, page, or search
  // that was on screen when the sync began.
  const refreshAttendeeListRef = useRef(refreshAttendeeList)
  useEffect(() => {
    refreshAttendeeListRef.current = refreshAttendeeList
  })

  /**
   * Polls every in-flight single attendee sync until it finishes.
   *
   * The row-level Resync button only creates the sync task; the sync itself runs in a
   * background worker. Without this poll the row stays on "Resyncing..." forever and a
   * failure - an invalid external ID, or a rejected registration API request - is never
   * shown to the user.
   */
  useEffect(() => {
    if (_.isEmpty(singleAttendeeSyncs)) return undefined

    const interval = setInterval(async () => {
      const checks = await Promise.all(
        _.map(singleAttendeeSyncs, async (sync) => ({
          sync,
          status: await getAttendeeSyncStatus(eventId, sync.id),
        })),
      )

      // Ids this pass is done with. Anything else is left alone, including a sync the
      // user started while these reads were in flight.
      const settledIds = []
      let syncedAnyAttendee = false

      _.forEach(checks, ({ sync, status }) => {
        // `getAttendeeSyncStatus` answers null when the read itself fails. Keep polling
        // through a brief outage, but give up rather than poll a task forever.
        if (!status) {
          const misses = (syncPollMissesRef.current[sync.id] || 0) + 1
          syncPollMissesRef.current[sync.id] = misses

          if (misses >= MAX_SINGLE_SYNC_POLL_MISSES) {
            delete syncPollMissesRef.current[sync.id]
            settledIds.push(sync.id)
            handleErrors(
              `Could not read the sync status for attendee ${sync.externalId}. Reload to check it.`,
            )
          }

          return
        }

        delete syncPollMissesRef.current[sync.id]

        if (_.includes(IN_PROGRESS_SYNC_STATUSES, status.status)) return

        settledIds.push(sync.id)

        if (status.status === 'Completed') {
          syncedAnyAttendee = true
          handleSuccesses(status.statusMessage || `Synced attendee ${sync.externalId}`)
        } else {
          handleErrors(
            `Failed to sync attendee ${sync.externalId}: ${status.statusMessage || status.status}`,
          )
        }
      })

      // Only write state when something actually settled, so the interval is not torn
      // down and rebuilt on every tick.
      if (!_.isEmpty(settledIds)) {
        setSingleAttendeeSyncs((prev) => _.reject(prev, (sync) => _.includes(settledIds, sync.id)))
      }

      if (syncedAnyAttendee) refreshAttendeeListRef.current()
    }, SINGLE_SYNC_POLL_MS)

    return () => clearInterval(interval)
  }, [singleAttendeeSyncs])

  /**
   * Updates the search query based on `search`.
   * @param {string} search
   */
  const updateSearch = (search) => {
    let updatedFilter = ''
    if (search) updatedFilter = `q=${search}`

    setSearchQuery(updatedFilter)
  }

  const filterAttendees = useCallback(_.debounce(updateSearch, 500), [])

  /**
   * Handles triggering a registration sync.
   */
  const triggerRegistrationSync = async (payload, singleAttendee = false) => {
    const response = await createAttendeeSync(
      eventId,
      payload,
      handleErrors,
      setLoadingSync,
      () => {},
    )

    if (response) {
      if (!singleAttendee) {
        setAttendeeSyncTask(response)
        setPingSync(true)
        handleSuccesses('Attendee sync triggered.')
      } else {
        setSingleAttendeeSyncs((prev) => [...prev, response])
        handleSuccesses('Attendee sync triggered.')
      }
    }
  }

  /**
   * Toggles the integrity test flag for an attendee.
   */
  const toggleIntegrityTest = async (attendee) => {
    const response = await updateAttendee(
      eventId,
      { id: attendee.id, isIntegrityTest: !attendee.isIntegrityTest },
      handleErrors,
      () => {},
      () => {},
    )

    if (response) {
      setAttendees((prev) =>
        prev.map((a) =>
          a.id === attendee.id ? { ...a, isIntegrityTest: response.isIntegrityTest } : a,
        ),
      )
    }
  }

  const sortedCategoryNames = _.sortBy(_.values(event?.attendeeCategories))
  const columns = [
    {
      id: 'name',
      grow: 1,
      name: 'Name',
      selector: (row) => `${row.firstName} ${row.lastName}`,
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-sm font-bold text-dark">
            {row.firstName} {row.lastName}
          </span>

          <span className="text-xs text-gray-600">{row.email}</span>
        </div>
      ),
      sortable: true,
      sortBy: 'first_name',
    },
    {
      id: 'confirmationCode',
      grow: 1.5,
      name: 'Confirmation Code',
      selector: (row) => row.confirmationCode,
      cell: (row) => (
        <div className="flex items-center justify-center">
          {row.confirmationCode ? (
            <span className="text-sm text-dark">{row.confirmationCode}</span>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>
      ),
      center: true,
      sortable: true,
      sortBy: 'confirmation_code',
    },
    {
      id: 'category',
      grow: 1,
      name: 'Category',
      selector: (row) => row.categoryName,
      cell: (row) => {
        if (!event?.attendeeCategories || !row.categoryId) return null

        const category = event?.attendeeCategories[row.categoryId]
        const index = _.findIndex(sortedCategoryNames, (k) => k === category)

        let displayColors
        if (index === -1) {
          displayColors = { background: colors.gray.light, label: colors.gray.dark }
        } else {
          displayColors = CATEGORY_COLORS[index] || {
            background: colors.gray.light,
            label: colors.gray.dark,
          }
        }

        return (
          <div className="flex w-full items-center justify-center">
            <span
              className="rounded-full px-2.5 py-1 text-xs font-medium"
              style={{ backgroundColor: displayColors.background, color: displayColors.label }}
            >
              {_.capitalize(category) || 'No Category'}
            </span>
          </div>
        )
      },
      center: true,
      sortable: true,
      sortBy: 'category_name',
      omit: active.id === 1, // Hide in secondary tab
    },
    {
      id: 'checkedInAt',
      grow: 0.75,
      name: 'Checked In',
      selector: (row) => row.checkedInAt,
      cell: (row) => (
        <div className="flex items-center justify-center">
          {row.checkedInAt ? (
            <span className="text-sm text-dark">
              {dayjs(row.checkedInAt).format('MM/DD/YYYY h:mm A')}
            </span>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>
      ),
      center: true,
      sortable: true,
      sortBy: 'checked_in_at',
    },
    {
      id: 'companyName',
      grow: 0.75,
      name: 'Company/Title',
      selector: (row) => row.companyName,
      cell: (row) => (
        <div className="flex flex-col">
          {!row.companyName && !row.title ? (
            <span className="text-xs text-gray-600">N/A</span>
          ) : (
            <>
              <span className="text-sm font-bold text-dark">{row.companyName}</span>

              <span className="text-xs text-gray-600">{row.title}</span>
            </>
          )}
        </div>
      ),
      sortable: true,
      sortBy: 'company_name',
    },
    {
      id: 'lastSyncedAt',
      grow: 0.75,
      name: 'Last Synced',
      selector: (row) => row.lastSyncedAt,
      cell: (row) => (
        <div className="flex items-center justify-center">
          {row.lastSyncedAt ? (
            <span className="text-sm text-dark">
              {dayjs(row.lastSyncedAt).format('MM/DD/YYYY h:mm A')}
            </span>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>
      ),
      center: true,
      sortable: true,
      sortBy: 'last_synced_at',
      omit: !event?.enableRegistrationSync || active.id === 1,
    },
    {
      id: 'isIntegrityTest',
      grow: 0.3,
      name: <BeakerIcon className="h-4 w-4 text-gray-600" />,
      selector: (row) => row.isIntegrityTest,
      cell: (row) => (
        <div className="flex items-center justify-center">
          <Tooltip
            content={
              <div className="rounded-lg bg-white px-2 pb-0.5">
                <span className="text-xs">
                  {row.isIntegrityTest ? 'Remove Integrity Test' : 'Mark as Integrity Test'}
                </span>
              </div>
            }
            display
            placement="top"
          >
            <button
              className="rounded-full p-1 hover:bg-status-blue"
              type="button"
              onClick={() => toggleIntegrityTest(row)}
            >
              <BeakerIcon
                className={`h-5 w-5 ${
                  row.isIntegrityTest ? 'text-purple' : 'text-gray-400 hover:text-purple'
                }`}
              />
            </button>
          </Tooltip>
        </div>
      ),
      center: true,
      sortable: true,
      sortBy: 'is_integrity_test',
      omit: !isEEUser || active.id === 1,
    },
    {
      id: 'actions',
      grow: 0.6,
      name: '',
      cell: (row) => {
        const matchingSyncTask = _.find(
          singleAttendeeSyncs,
          (t) => t.externalId === row.externalId,
        )

        return (
          <div className="flex items-center gap-2">
            {/* Per-attendee resync is granted to org users (EXEC-1543) */}
            {event?.enableRegistrationSync && (
              <button
                className={BUTTON_STYLES}
                disabled={!!matchingSyncTask}
                type="button"
                onClick={() => {
                  triggerRegistrationSync(
                    {
                      syncType: 'Attendee - Single',
                      externalId: row.externalId,
                    },
                    true,
                  )
                }}
              >
                {matchingSyncTask ? 'Resyncing...' : 'Resync'}
              </button>
            )}

            <button
              className={BUTTON_STYLES}
              disabled={row.id === user.id}
              type="button"
              onClick={() => {
                setEditAttendee(row)
                setShowAttendeeModal(true)
              }}
            >
              {readOnly ? 'View' : 'Edit'}
            </button>
          </div>
        )
      },
      center: true,
      omit: (!isOrganizationAdmin(orgId) && !isEEUser) || active.id === 1,
    },
  ]

  const renderFileUploader = () => {
    if (uploadFile) {
      return (
        <div className="flex flex-col">
          <FileUploader
            acceptedFileTypes={[
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ]}
            autoSave
            handleUploadToServer={async (file) => {
              setImportedFile(file)
              setUploadFile(null)
            }}
            id="file"
            maxFiles={1}
            type={null}
          />

          <div className="-mt-4 mr-2 flex flex-row place-items-center justify-between">
            <span className="text-xs italic text-gray-500">
              Upload a single file that is .xlsx or .xlsm
            </span>

            <button className="place-self-end" type="button" onClick={() => setUploadFile(null)}>
              <span className="text-xs">Cancel Upload</span>
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="flex flex-row items-center space-x-4 self-center">
        <span className="text-sm text-gray-500">
          {importedFile ? importedFile.name : 'Upload File'}
        </span>

        <Button
          background="bg-white"
          icon={<img src={Inbox} alt="Upload" className="h-5" />}
          label={importedFile ? 'Change' : 'Upload'}
          onClick={() => setUploadFile(true)}
          outlined
        />
      </div>
    )
  }

  const canTriggerSync = readOnly && hasCompletedRegistrationConfig(event, registrationOptions)

  // Org users may only resync attendee updates on Stova-synced events — every other
  // registration system stays EE-only, as do full resyncs everywhere (EXEC-1543). The API
  // enforces both independently of this.
  const canResyncUpdates = isEEUser || event?.registrationType === 'Stova'

  return (
    <div className="h-full w-full">
      <StateContainer loading={loading}>
        <div className="h-full w-full flex-col overflow-y-auto p-3">
          <div className="flex flex-col items-start justify-between space-y-1 sm:flex-row sm:space-y-0">
            <EventHeader event={event} />

            <div className="flex flex-col items-end space-y-1">
              <div className="flex flex-row items-center gap-2">
                {/* Export Check-In Report is granted to org users (EXEC-1543) */}
                <Button
                  background="bg-purple border-purple hover:bg-purple-600"
                  icon={<DownloadIcon className="h-5 stroke-white sm:h-5" />}
                  label="Export Check-In Report"
                  loading={loadingCheckInExport}
                  onClick={async () => {
                    const exportTask = await createCheckInExport(
                      eventId,
                      handleErrors,
                      setLoadingCheckInExport,
                    )

                    if (exportTask) {
                      setType('Download')
                      setParent({
                        type: 'check-in-export',
                        label: 'Check-In Report',
                        id: eventId,
                        fileName: `${event.name}_Check_In_Report`,
                      })
                      setTask(exportTask)
                    }
                  }}
                />

                {!readOnly && isOrganizationAdmin(orgId) && isEEUser && (
                  <Button
                    background="bg-purple border-purple hover:bg-purple-600"
                    icon={<ArrowUpTrayIcon className="h-5 stroke-white sm:h-5" />}
                    label="Import Attendees"
                    onClick={() => setShowImportModal(true)}
                  />
                )}
              </div>

              {canTriggerSync && (
                <div className="flex flex-col space-y-1">
                  <Tooltip
                    content={
                      <div className="rounded-lg bg-white px-2 pb-0.5">
                        <span className="text-xs">Sync is in progress.</span>
                      </div>
                    }
                    display={attendeeSyncTask}
                    fullWidth
                    placement="left"
                  >
                    <Menu>
                      {canResyncUpdates ? (
                        <div className="flex flex-row gap-[1px]">
                          <Button
                            background="bg-purple border-purple hover:bg-purple-600 rounded-r-none"
                            disabled={attendeeSyncTask}
                            fullWidth
                            loading={loadingSync}
                            label="Resync Attendee Updates"
                            onClick={() =>
                              triggerRegistrationSync({ syncType: 'Attendees - Updates Only' })
                            }
                          />

                          <MenuButton className="rounded-r-md bg-purple px-1 hover:bg-purple-600">
                            <ChevronDownIcon className="h-5 stroke-white" />
                          </MenuButton>
                        </div>
                      ) : (
                        // Without the resync action there is no primary button to split, so the
                        // control collapses to a plain menu rather than a headless caret.
                        <MenuButton className="flex flex-row items-center gap-1 rounded-md bg-purple px-3 py-2 text-sm font-semibold text-white hover:bg-purple-600">
                          Sync Options
                          <ChevronDownIcon className="h-5 stroke-white" />
                        </MenuButton>
                      )}

                      <MenuItems
                        anchor="bottom end"
                        className={joinClassNames(
                          'rounded-md bg-white [--anchor-gap:2px]',
                          event?.enableSecondaryRegistrationSync ? 'w-72' : 'w-52',
                        )}
                      >
                        {/* Full resyncs are EE-only (EXEC-1543) */}
                        {isEEUser && (
                          <Button
                            background="bg-purple border-purple right-0 hover:bg-purple-600 rounded-b-none mb-[1px]"
                            disabled={attendeeSyncTask || event?.disableSyncAllButton}
                            fullWidth
                            loading={loadingSync}
                            label="Resync All Attendees"
                            onClick={() =>
                              triggerRegistrationSync({ syncType: 'Attendees - All Time' })
                            }
                          />
                        )}

                        <Button
                          background={joinClassNames(
                            'bg-purple border-purple right-0 hover:bg-purple-600 mb-[1px]',
                            isEEUser ? 'rounded-none' : 'rounded-b-none',
                          )}
                          fullWidth
                          label="Sync External Attendee"
                          onClick={() => setShowSyncExternalAttendeeModal(true)}
                        />

                        <Button
                          background="bg-purple border-purple right-0 hover:bg-purple-600 rounded-t-none"
                          fullWidth
                          label="View Sync Log"
                          onClick={() => setShowSyncLog(true)}
                        />
                      </MenuItems>
                    </Menu>
                  </Tooltip>

                  {event.attendeesLastSyncedAt && !event.attendeeSyncStatusMessage && (
                    <span className="self-end text-xs">
                      Last Updated {dayjs(event.attendeesLastSyncedAt).format('MM/DD/YYYY h:mm A')}
                    </span>
                  )}

                  {event?.currentSyncInterval && (
                    <span className="self-end text-xs italic">
                      Syncing every {formatDuration(event.currentSyncInterval)}.
                    </span>
                  )}

                  {event.attendeesSyncStatusMessage && (
                    <span className="text-xs text-error">{event.attendeesSyncStatusMessage}</span>
                  )}
                </div>
              )}

              {event && event.attendeeListModifiedAt && (
                <span className="text-xs font-semibold">
                  Last {readOnly ? 'Sync' : 'Import'}:{' '}
                  {dayjs(event.attendeeListModifiedAt).format('MM/DD/YYYY h:mm A')}
                </span>
              )}
            </div>
          </div>

          <span className="text-md font-bold">Attendees</span>

          <div className="my-3 flex w-full flex-row items-center justify-between">
            <TextInput
              className="w-full rounded-full py-2.5 pl-10 pr-4 placeholder:font-normal placeholder:text-gray-600 md:w-[450px]"
              icon={<MagnifyingGlassIcon className="ml-2 h-5 text-gray-dark" aria-hidden="true" />}
              id="search"
              endIcon={
                searchTerm ? (
                  <button
                    type="button"
                    onClick={() => {
                      filterAttendees('')
                      setSearchTerm('')
                      searchInputRef.current.value = ''
                    }}
                  >
                    <XMarkIcon className="mr-2 h-5 text-gray-dark" aria-hidden="true" />
                  </button>
                ) : null
              }
              name="search"
              onChange={(e) => {
                filterAttendees(e.target.value)
                setSearchTerm(e.target.value)
              }}
              placeholder="Looking for something?"
              ref={searchInputRef}
              value={searchTerm}
            />

            <div className="flex flex-row items-center gap-2">
              <Button
                background="bg-purple border-purple hover:bg-purple-600"
                disabled={loadingAttendees}
                icon={<ArrowPathIcon className="h-5 sm:h-6" />}
                label="Refresh"
                loading={loadingAttendees}
                onClick={refreshAttendeeList}
              />

              {!readOnly && isOrganizationAdmin(orgId) && isEEUser && (
                <Button
                  background="bg-purple border-purple hover:bg-purple-600"
                  icon={<PlusIcon className="h-5 sm:h-6" />}
                  label="Add Attendee"
                  onClick={() => setShowAttendeeModal(true)}
                />
              )}
            </div>
          </div>

          {event?.enableSecondaryRegistrationSync && isEEUser && (
            <div className="px-3">
              <nav className="flex" aria-label="Tabs">
                {TABS.map((tab) => {
                  const activeTab = active.id === tab.id

                  return (
                    <button
                      type="button"
                      key={tab.label}
                      onClick={() => {
                        // Reset pagination when the active tab changes
                        setPrimaryCurrentPage(1)
                        setPrimaryTotalRows(0)
                        setPrimaryPages(null)

                        setActive(tab)
                      }}
                      className={joinClassNames(
                        activeTab
                          ? 'border-purple-700 bg-[#8c56f626] text-purple-700'
                          : 'border-b-transparent text-gray-600 hover:border-purple-700 hover:bg-[#8c56f626] hover:text-purple-700',
                        'group relative flex w-1/2 items-center justify-center rounded-t-[10px] border-b-2 px-4 py-2 text-sm font-medium',
                      )}
                      aria-current={activeTab ? 'page' : undefined}
                    >
                      {tab.label}
                    </button>
                  )
                })}
              </nav>
            </div>
          )}
          <div style={{ height: 'calc(100vh - 300px)' }}>
            <DataTable
              columns={columns}
              data={attendees}
              defaultSortFieldId="name"
              defaultSortAsc
              onChangePage={(page) =>
                handlePagination(
                  page,
                  primaryCurrentPage,
                  primaryPerPage,
                  primaryTotalRows,
                  primaryPages,
                  setPrimaryCurrentPage,
                  getUpdatedAttendeeList,
                  `${BASE_URL}limit=`,
                  searchQuery,
                )
              }
              onChangeRowsPerPage={async (currentRowsPerPage) =>
                setPrimaryPerPage(currentRowsPerPage)
              }
              onSort={(column, direction) => {
                const d = direction === 'asc' ? '' : '-'
                const url = `${BASE_URL}order_by=${d}${column.sortBy}&limit=${primaryPerPage}`
                getUpdatedAttendeeList(url)
              }}
              pagination
              paginationPerPage={primaryPerPage}
              paginationRowsPerPageOptions={[10, 15, 20, 30, 50]}
              paginationTotalRows={primaryTotalRows}
              paginationServer
              progressPending={loadingAttendees}
              sortServer
            />
          </div>
        </div>
      </StateContainer>

      {showAttendeeModal && (
        <AttendeeModal
          attendee={editAttendee}
          availableCustomFields={event?.availableCustomFields}
          categories={categories}
          eventId={eventId}
          enabledCustomFields={event?.enabledCustomFields}
          enableRegistrationSync={event?.enableRegistrationSync}
          onClose={(refresh = false) => {
            setEditAttendee(null)
            setShowAttendeeModal(false)

            if (refresh) getUpdatedAttendeeList(`${BASE_URL}limit=${primaryPerPage}`)
          }}
          sessions={event?.sessions}
        />
      )}

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Cancel',
            onClick: () => {
              setUploadFile(null)
              setImportedFile(null)
            },
          },
          {
            type: 'submit',
            label: 'Import',
            disabled: !importedFile,
            onClick: async () => {
              const response = await uploadAttendeeList(
                eventId,
                { file: importedFile.file },
                handleErrors,
                setLoadingImport,
              )

              if (response) {
                // Clear & close the modal
                setUploadFile(null)
                setImportedFile(null)
                setShowImportModal(false)

                // Set up the task to watch
                setType('Upload')
                setParent({ type: 'attendee-import', id: response.event })
                setTask(response)
              }
            },
          },
        ]}
        icon={<DownloadIcon className="h-5 stroke-white sm:h-6" />}
        content={
          <div className="mt-3 flex flex-col space-y-4 text-center sm:mt-5">
            <Button
              background="bg-green-50"
              className="self-center"
              label="Download Template"
              onClick={() => {
                // Download the template
                const downloadLink = document.createElement('a')
                downloadLink.href = template
                downloadLink.download = 'Executivevents Attendee Import Template.xlsx'
                downloadLink.target = '_blank'
                downloadLink.click()
                downloadLink.remove()
              }}
            />

            {renderFileUploader()}
          </div>
        }
        loading={loadingImport}
        onClose={() => {
          setShowImportModal(false)
        }}
        open={showImportModal}
        setOpen={setShowImportModal}
        title="Import"
      />

      {showSyncExternalAttendeeModal && (
        <SyncExternalAttendeeModal
          eventId={eventId}
          onClose={(refresh = false) => {
            setShowSyncExternalAttendeeModal(false)

            if (refresh) getUpdatedAttendeeList(`${BASE_URL}limit=${primaryPerPage}`)
          }}
        />
      )}

      <SyncLogSlideout
        eventId={eventId}
        registrationType={event?.registrationType}
        open={showSyncLog}
        onClose={() => setShowSyncLog(false)}
      />
    </div>
  )
})

export default EventAttendees
